import ChipPressableComponent from "../Chips/ComponentChipPressable/Index";
import { useEditOnboarding } from "../../hooks/useEditOnboarding";
import { useTaskCreation } from "../../hooks/useTaskCreation";
import TableTaskEvaluation from "../Table/TaskEvaluation";
import TableEvaluation from "../Table/PastureEvaluator";
import ComponentInputSearch from "../InputSearch";
import { useFilter } from "../../hooks/useFilter";
import { DateModal } from "../Modal/DateFilter";
import TableProducts from "../Table/Products";
import { useEffect, useState } from "react";
import axios from "../../services/axios";
import Pagination from "../Pagination";
import { format } from "date-fns";
import "./styles.css"
import FailSyncTable from "../Table/FailSync/Index";
import { useTableMonitor } from "../../hooks/useTableMonitor";

export type IOptionsEvaluation =
  | "FINISHED"
  | "COMPLETED"
  | "PENDING"
  | "ERROR"
  | "ARCHIVE"
  | "CANCELED"
  | "ACCEPTED"

export function ViewTables() {
  const {
    queryToSearch,
    setQueryToSearch,
    setFilterSelected
  } = useEditOnboarding();
  const { modalStartEvaluator } = useTaskCreation();
  const { selectedOrder, sortAsc } = useTableMonitor();

  const [searchable, setSearchable] = useState(true);
  const [havePagination, setHavePagination] = useState(false);
  const [listTasks, setListTasks] = useState<ITaskEvaluation[]>([]);
  const [listEvaluations, setListEvaluations] = useState<IMicroAreaEvaluation[]>([]);
  const [listProducts, setListProducts] = useState<IProducts[]>([]);
  const [listFailsSync, setListFailSync] = useState<IFailSync[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<IOptionsEvaluation | undefined>(undefined);
  const { sideBarOptionSelected, systemManager, rangeDays, reloadTasks } = useFilter();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const quantity = new Map<string, number>()
  quantity.set("PRODUCTS", 8)
  quantity.set("PASTURE_EVALUATION", 7)
  quantity.set("TASK_EVALUATION", 7)
  quantity.set("FAIL_SYNC", 7)

  const [optionSelected, setOptionSelected] = useState<null | string>(null);
  useEffect(() => {
    if (sideBarOptionSelected == "MANAGER_SYSTEM") {
      setOptionSelected(systemManager)
    } else if (sideBarOptionSelected == "PASTURE_EVALUATION" && systemManager) {
      setOptionSelected(systemManager)
    } else {
      setOptionSelected(sideBarOptionSelected)
    }
  }, [systemManager, sideBarOptionSelected]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let didCancel = false;

    setIsLoading(true);

    const page = currentPage - 1;
    const size = optionSelected ? quantity.get(optionSelected) : 7;
    const dateBegin = rangeDays?.from ? format(new Date(rangeDays.from), "y-MM-dd") : undefined;
    const dateEnd = rangeDays?.to ? format(new Date(rangeDays.to), "y-MM-dd") : undefined;

    if (optionSelected === "PRODUCTS") {
      setSearchable(false);
      axios
        .get(`products`, {
          params: {
            size,
            page,
          },
          signal,
        })
        .then(({ data }) => {
          setListProducts(data.content);
          setTotalPages(data.total_pages);
          setHavePagination(data.total_pages > 0);
        })
        .finally(() => { if (!didCancel) setIsLoading(false); });
    } else if (optionSelected === "PASTURE_EVALUATION") {
      setFilterSelected("evaluations");
      setSearchable(true);
      axios
        .get(`tasks/pasture-evaluation`, {
          params: {
            ...(filters ? { status: filters } : {}),
            size,
            page,
            ...(queryToSearch ? { query: queryToSearch } : {}),
            ...(dateBegin ? { date_begin: dateBegin } : {}),
            ...(dateEnd ? { date_end: dateEnd } : {}),
            sort_by: selectedOrder,
            sort_direction: sortAsc ? "asc" : "desc",
          },
          signal,
        })
        .then(({ data }) => {
          setListEvaluations(data.content);
          setTotalPages(data.total_pages);
          setHavePagination(data.total_pages > 0);
        })
        .finally(() => { if (!didCancel) setIsLoading(false); });
    } else if (optionSelected === "TASK_EVALUATION") {
      setFilterSelected("task-evaluations");
      setSearchable(true);

      if (filters !== "ERROR") {
        let extraParams: Record<string, any> = {};
        try {
          const taskTypeEnumObj = JSON.parse(localStorage.getItem("TaskTypeEnumObj") || "{}");
          if (queryToSearch && typeof queryToSearch === "string") {
            const matchedKey = Object.keys(taskTypeEnumObj).find(
              key => taskTypeEnumObj[key] === queryToSearch
            );
            if (matchedKey) {
              extraParams.task_type = matchedKey;
            } else {
              extraParams.query = queryToSearch;
            }
          }
        } catch (e) {
          if (queryToSearch) extraParams.query = queryToSearch;
        }

        axios
          .get(`tasks/monitor`, {
            params: {
              ...(filters ? { status: filters } : {}),
              size,
              page,
              ...extraParams,
              ...(dateBegin ? { date_begin: dateBegin } : {}),
              ...(dateEnd ? { date_end: dateEnd } : {}),
              sort_by: selectedOrder,
              sort_direction: sortAsc ? "asc" : "desc",
            },
            signal,
          })
          .then(({ data }) => {
            setListTasks(data.content);
            setTotalPages(data.total_pages);
            setHavePagination(data.total_pages > 0);
          })
          .finally(() => { if (!didCancel) setIsLoading(false); });
      } else {
        setIsLoading(true);
        setSearchable(false);
        axios
          .get(`sync/failures`, {
            params: {
              size,
              page,
              sort_by: selectedOrder,
              sort_direction: sortAsc ? "asc" : "desc",
            },
            signal,
          })
          .then(({ data }) => {
            setListFailSync(
              data.content.map((el: any) => ({
                id: el.id,
                date: new Date(el.created_at),
                name: el.name,
              }))
            );
            setTotalPages(data.total_pages);
            setHavePagination(data.total_pages > 0);
          })
          .finally(() => { if (!didCancel) setIsLoading(false); });
      }
    }

    return () => {
      didCancel = true;
      controller.abort();
    };
  }, [
    modalStartEvaluator,
    optionSelected,
    currentPage,
    queryToSearch,
    filters,
    rangeDays,
    reloadTasks,
    selectedOrder,
    sortAsc,
  ]);

  useEffect(() => {
    setCurrentPage(1)
  }, [filters, rangeDays, queryToSearch]);

  useEffect(() => {
    setCurrentPage(1)
  }, [modalStartEvaluator]);

  return (
    <>
      {/* Filtro */}
      <div className="filter__content">
        {optionSelected == "PASTURE_EVALUATION" && (
          <>
            <ChipPressableComponent
              theme={
                filters == undefined ? "menu-dark" : "menu-light"
              }
              label={"Todos"}
              onClick={() => setFilters(undefined)}
            />
            <ChipPressableComponent
              theme={filters == "COMPLETED" ? "menu-dark" : "menu-light"}
              label={"Avaliado"}
              onClick={() => setFilters("COMPLETED")}
            />
            <ChipPressableComponent
              theme={
                filters == "ARCHIVE" ? "menu-dark" : "menu-light"
              }
              label={"Arquivados"}
              onClick={() => setFilters("ARCHIVE")}
            />
            <ChipPressableComponent
              theme={
                filters == "CANCELED" ? "menu-dark" : "menu-light"
              }
              label={"Cancelados"}
              onClick={() => setFilters("CANCELED")}
            />
            <ChipPressableComponent
              theme={
                filters == "PENDING" ? "menu-dark" : "menu-light"
              }
              label={"Pendente"}
              onClick={() => setFilters("PENDING")}
            />
          </>
        )}
        {optionSelected == "TASK_EVALUATION" && (
          <>
            <ChipPressableComponent
              theme={
                filters == undefined ? "menu-dark" : "menu-light"
              }
              label={"Todos"}
              onClick={() => setFilters(undefined)}
            />
            <ChipPressableComponent
              theme={filters == "COMPLETED" ? "menu-dark" : "menu-light"}
              label={"Concluido"}
              onClick={() => setFilters("COMPLETED")}
            />
            <ChipPressableComponent
              theme={
                filters == "ERROR" ? "menu-dark" : "menu-light"
              }
              label={"Falha de sincronismo"}
              onClick={() => setFilters("ERROR")}
            />
            <ChipPressableComponent
              theme={
                filters == "PENDING" ? "menu-dark" : "menu-light"
              }
              label={"Pendente"}
              onClick={() => setFilters("PENDING")}
            />
          </>
        )}

        {searchable && <>
          <div style={{ width: "100%" }}>
            <ComponentInputSearch
              onClick={(query: string | undefined) => setQueryToSearch(query)}
              onChange={el => {
                if (el == "") {
                  setQueryToSearch(undefined)
                }
              }}
            />
          </div>
        </>}
      </div>
      {/* Filtro */}

      {/* Visualização dos avaliadores */}
      <div className="tableContainer">
        {optionSelected == "TASK_EVALUATION" && filters != "ERROR" ? <TableTaskEvaluation listTable={listTasks} filter={filters} isLoading={isLoading} /> : <></>}
        {optionSelected == "TASK_EVALUATION" && filters == "ERROR" ? <FailSyncTable listTable={listFailsSync} isLoading={isLoading} /> : <></>}
        {optionSelected == "PASTURE_EVALUATION" ? <TableEvaluation listTable={listEvaluations} filter={filters} isLoading={isLoading} /> : <></>}
        {optionSelected == "PRODUCTS" ? <TableProducts listTable={listProducts} isLoading={isLoading} /> : <></>}
      </div>
      {/* Visualização dos avaliadores */}

      {/* Paginação */}
      {havePagination && (
        <>
          <div className="delimiter" style={{ marginTop: 30, marginBottom: 20 }} />
          <div className="paginator">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onClick={(value: number) => setCurrentPage(value)}
            />
          </div>
        </>
      )}
      {/* Paginação */}

      {/*Modals*/}
      <DateModal />
      {/*Modals*/}
    </>
  );
}
